import { FACEBOOK_AUTOMATION_STAGE_STATUSES } from './facebookAutomationStageStatuses'
import * as selectAudienceUtils from './fbSelectAudienceModalUtils'
import {
  clickSettingButton,
  closeSettingModal,
  getSettingModalSelectedPrivacyLevel,
  openClickableSettingModal,
  setSettingModalPrivacyLevel,
} from './fbSettingsUtils'
import { postsUrl } from './fbUrls'
import { FacebookSettingKey } from '../../recommendations/recommendationSettingsKeys'
import * as enums from '../../utils/enums'
import { StageDef } from '../automationMachineInterfaces'
import * as promises from '../interruptiblePromises'
import {
  defaultCantUndoFn,
  getErrorIsDisabledAndReasonFn,
  getCurrentValueFnFromKey,
  getIsFulfilledFnFromKey,
} from '../recommendationCardDisplayUtils'
import { getElement, shouldUpdateSetting } from '../utils'
import { getCaseInsensitiveXPathTextSelector } from '../xpath'

// Setting property function
const currentValueFns = {
  [FacebookSettingKey.VISIBILITY_FUTURE_POSTS]: getCurrentValueFnFromKey(
    FacebookSettingKey.VISIBILITY_FUTURE_POSTS,
    (value) => value || null,
  ),
  [FacebookSettingKey.VISIBILITY_PAST_POSTS]: () => null,
}

const isFulfilledFns = {
  [FacebookSettingKey.VISIBILITY_FUTURE_POSTS]: getIsFulfilledFnFromKey(
    FacebookSettingKey.VISIBILITY_FUTURE_POSTS,
    (value) => {
      const currentSetting = {
        name: value,
        level: selectAudienceUtils.getPrivacySettingFromLabel(value),
      }

      return selectAudienceUtils.isPrivacySettingAsStrictOrStricter(
        currentSetting,
        selectAudienceUtils.FRIENDS,
      )
    },
  ),
  [FacebookSettingKey.VISIBILITY_PAST_POSTS]: () => false,
}

const cantUndoFns = {
  [FacebookSettingKey.VISIBILITY_FUTURE_POSTS]: defaultCantUndoFn,
  [FacebookSettingKey.VISIBILITY_PAST_POSTS]: () => true,
}

const getCurrentValueFn = (key) => currentValueFns[key]
const getIsFulfilledFn = (key) => isFulfilledFns[key]
const getCantUndoFn = (key) => cantUndoFns[key]
function getIsDisabledFn(settingsKey: string) {
  const fn = getErrorIsDisabledAndReasonFn(settingsKey)
  return (automationErrors) => fn(automationErrors).isDisabled
}
function getIsDisabledReasonFn(settingsKey: string) {
  const fn = getErrorIsDisabledAndReasonFn(settingsKey)
  return (automationErrors) => fn(automationErrors).isDisabledReason
}

export const FUTURE_POSTS_SETTING_LABEL = 'Who can see your future posts?'
export const FUTURE_POSTS_MODAL_LABEL = 'Who can see your future posts?'

export async function clickModalButton(modalTitleText: string, label: string) {
  const modalTitle = await getElement<HTMLElement>(
    document,
    `//*[text()="${modalTitleText}"]`,
  )

  const button = await getElement<HTMLElement>(
    modalTitle,
    `following::div[${getCaseInsensitiveXPathTextSelector(
      'aria-label',
      label.toLowerCase(),
    )} and @role="button"]`,
  )

  button.click()
  await promises.wait(200)
}

// Gather stage

enum StageKey {
  GATHER_SETTING_FUTURE_POSTS_OPTION = 'GATHER_SETTING_FUTURE_POSTS_OPTION',
  UPDATE_SETTING_FUTURE_PAST_POSTS_OPTION = 'UPDATE_SETTING_FUTURE_PAST_POSTS_OPTION',
  UNDO_SETTING_FUTURE_POSTS_OPTION = 'UNDO_SETTING_FUTURE_POSTS_OPTION',
}

const gatherStageDefs: StageDef[] = [
  {
    type: enums.StageType.ACTION,
    key: StageKey.GATHER_SETTING_FUTURE_POSTS_OPTION,
    status: FACEBOOK_AUTOMATION_STAGE_STATUSES.GATHERING_SETTINGS,
    url: postsUrl,
    action: async (machineContext, userContext) => {
      const { updateStoredInitialValues } = userContext

      await openClickableSettingModal([FUTURE_POSTS_SETTING_LABEL])
      const futurePostsSetting = await getSettingModalSelectedPrivacyLevel(
        FUTURE_POSTS_MODAL_LABEL,
      )
      await closeSettingModal(FUTURE_POSTS_MODAL_LABEL, 'CANCEL', {
        removeModalManually: true,
      })

      await updateStoredInitialValues(
        {
          [FacebookSettingKey.VISIBILITY_FUTURE_POSTS]: futurePostsSetting.name,
        },
        enums.FBRecommendationKey.FB_VISIBILITY,
      )

      return true
    },
  },
]

const updateStageDefs: StageDef[] = [
  {
    type: enums.StageType.ACTION,
    key: StageKey.UPDATE_SETTING_FUTURE_PAST_POSTS_OPTION,
    status: FACEBOOK_AUTOMATION_STAGE_STATUSES.UPDATING_SETTINGS,
    url: postsUrl,
    action: async (machineContext, userContext) => {
      const { settingsSelected, initialValues, isDry } = userContext

      if (!settingsSelected) {
        return null
      }

      if (
        shouldUpdateSetting(
          settingsSelected,
          initialValues,
          FacebookSettingKey.VISIBILITY_FUTURE_POSTS,
          getIsFulfilledFn,
        )
      ) {
        await openClickableSettingModal([FUTURE_POSTS_SETTING_LABEL])

        await setSettingModalPrivacyLevel(
          FUTURE_POSTS_MODAL_LABEL,
          isDry,
          selectAudienceUtils.FRIENDS,
        )
      }

      if (settingsSelected[FacebookSettingKey.VISIBILITY_PAST_POSTS]) {
        await clickSettingButton('Limit past posts')
        await clickModalButton(
          'Limit who can see past posts on your profile',
          'Limit past posts',
        )
        if (isDry) {
          await clickModalButton('Limit all past posts?', 'Cancel')
        } else {
          await clickModalButton('Limit all past posts?', 'Limit past posts')
        }
      }
      return true
    },
  },
]

const undoStageDefs: StageDef[] = [
  {
    type: enums.StageType.ACTION,
    key: StageKey.UNDO_SETTING_FUTURE_POSTS_OPTION,
    status: FACEBOOK_AUTOMATION_STAGE_STATUSES.UNDOING_SETTINGS,
    url: postsUrl,
    action: async (machineContext, userContext) => {
      const { settingsSelected, initialValues, isDry } = userContext

      const prevValue =
        initialValues[FacebookSettingKey.VISIBILITY_FUTURE_POSTS]

      if (
        shouldUpdateSetting(
          settingsSelected,
          initialValues,
          FacebookSettingKey.VISIBILITY_FUTURE_POSTS,
          getIsFulfilledFn,
        )
      ) {
        await openClickableSettingModal([FUTURE_POSTS_SETTING_LABEL])
        const prevSetting = {
          name: prevValue,
          level: selectAudienceUtils.getPrivacySettingFromLabel(prevValue),
        }

        await setSettingModalPrivacyLevel(
          FUTURE_POSTS_MODAL_LABEL,
          isDry,
          prevSetting,
        )
      }
    },
  },
]

export {
  getCantUndoFn,
  getCurrentValueFn,
  getIsDisabledFn,
  getIsDisabledReasonFn,
  getIsFulfilledFn,
  gatherStageDefs,
  updateStageDefs,
  undoStageDefs,
}
